import React, { useState, useEffect } from "react";
import { Eye, EyeOff, Lock } from "lucide-react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { registerSchema } from "../../Validations/RegisterValidation";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setCredentials } from "../../Redux/slices/authSlice";
import { useRegisterMutation } from "../../Redux/api/authApi";
import { RiQuestionnaireFill } from "react-icons/ri";

const RegisterPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isAgent, setIsAgent] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [register, { isLoading, isSuccess, isError, error }] =
    useRegisterMutation();

  const {
    register: formRegister,
    handleSubmit,
    formState: { errors },
    reset: resetForm,
  } = useForm({
    resolver: yupResolver(registerSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  useEffect(() => {
    if (isSuccess) {
      toast.success("Registration successful! 🎉");
      resetForm();
      setIsAgent(false);
      navigate("/login");
    }

    if (isError) {
      const errorMessage = error?.data?.message || "Registration failed!";
      toast.error(errorMessage);
    }
  }, [isSuccess, isError, error, navigate, resetForm]);

  const onSubmit = async (data) => {
    try {
      const registrationData = {
        name: data.name,
        email: data.email,
        type: isAgent ? "agent" : "general",
        password: data.password,
      };

      const result = await register(registrationData).unwrap();

      if (result.access_token) {
        dispatch(setCredentials(result));
      }
    } catch (err) {
      console.error("Registration error:", err);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl w-full bg-white rounded-2xl border border-gray-200 shadow p-8">
        <div className="text-center mb-8">
          <div className="loginLogo text-center text-3xl font-extrabold tracking-wide text-[#6cb34e] mb-3">
            MB-SOURCE
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Create Account</h2>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          {/* 👇 Responsive grid: 1 column on mobile/tablet, 2 on large screens */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <input
                type="text"
                {...formRegister("name")}
                placeholder="Enter your full name"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#101b45] outline-none ${
                  errors.name ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.name.message}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                {...formRegister("email")}
                placeholder="Enter your email address"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#101b45] outline-none ${
                  errors.email ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  {...formRegister("password")}
                  placeholder="Create a strong password"
                  className={`w-full pl-10 pr-10 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#101b45] outline-none ${
                    errors.password ? "border-red-500" : "border-gray-300"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  {...formRegister("confirmPassword")}
                  placeholder="Re-enter your password"
                  className={`w-full pl-10 pr-10 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#101b45] outline-none ${
                    errors.confirmPassword
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>

            {/* Agent Toggle */}
            <div className="flex gap-1 justify-between items-center md:col-span-2">
              <div className="flex justify-between items-center w-full px-3 py-2 border rounded-lg">
                <span className="text-sm font-medium flex items-center gap-2">
                  {isAgent ? (
                    "Agent"
                  ) : (
                    <>
                      Want to become an Agent{" "}
                      <span className="relative group">
                        <RiQuestionnaireFill className="text-gray-400 hover:text-gray-600 text-lg w-5 h-5 cursor-pointer" />
                        <div className="hidden group-hover:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 rounded-md bg-gray-800 text-white text-xs text-left p-2 w-64 shadow-lg transition-all duration-200 z-50">
                          Upload and sell your products to business buyers
                          worldwide.
                          <br />
                          <span className="font-medium">
                            * A General account will also be created
                          </span>
                        </div>
                      </span>
                    </>
                  )}
                </span>

                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isAgent}
                    onChange={() => setIsAgent(!isAgent)}
                    className="sr-only peer"
                  />
                  <div
                    className="peer h-6 w-11 rounded-full border bg-slate-200 
            after:absolute after:left-[2px] after:top-0.5 after:h-5 after:w-5 after:rounded-full 
            after:border after:border-gray-300 after:bg-white after:transition-all 
            peer-checked:bg-[#54a958] peer-checked:after:translate-x-full 
            peer-checked:after:border-white"
                  ></div>
                </label>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex gap-3 justify-end pt-2">
            <button
              type="submit"
              disabled={isLoading}
              className={`px-4 py-2 bg-[#101b45] text-white rounded-full w-full hover:bg-[#0e1734] transition ${
                isLoading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isLoading ? "Creating..." : "Sign Up"}
            </button>
          </div>
        </form>

        <p className="mt-6 text-center text-sm text-gray-600">
          Already have an account?{" "}
          <Link
            to="/login"
            className="text-[#54a958] hover:text-[#89e064] font-extrabold"
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
